import { useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Loader2 } from "lucide-react"
import { useGetProspectsQuery } from "@apis/prospects/useProspects"
import { DataTable } from "@components/DataTable/DataTable"
import {
  Card,
  CardTitle,
  CardHeader,
  CardContent,
  CardAction,
} from "@components/ui/card"
import { PageTitle } from "@components/PageTitle"
import { usePaginationState } from "@hooks/usePaginationState"
import { useDebounce } from "@hooks/useDebounce"
import { useGetAccountsQuery } from "@apis/accounts/useAccounts"
import { Input } from "@components/ui/input"
import { Toggle } from "@components/ui/toggle"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/ui/tooltip"
import { uuidRegex } from "@utils/regex"
import { accountsColumns, prospectsColumns } from "./utils/tableColumnsSchema"

export const SearchV2 = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [inputValue, setInputValue] = useState<string>("")
  const [showAccounts, setShowAccounts] = useState<boolean>(true)
  const [showProspects, setShowProspects] = useState<boolean>(true)
  const accountsPagination = usePaginationState()
  const prospectsPagination = usePaginationState()
  const debouncedInputValue = useDebounce(inputValue)
  const navigate = useNavigate()

  const getAccountsQuery = useGetAccountsQuery(
    {
      query: searchParams.get("query") || "",
      page: accountsPagination.pagination.pageIndex + 1,
      per_page: accountsPagination.pagination.pageSize,
    },
    showAccounts,
  )
  const getProspectsQuery = useGetProspectsQuery(
    {
      query: searchParams.get("query") || "",
      page: prospectsPagination.pagination.pageIndex + 1,
      per_page: prospectsPagination.pagination.pageSize,
    },
    showProspects,
  )

  const processSearchValue = (value: string) => {
    if (!value) return ""

    let processedValue = value
    if (!processedValue.match(uuidRegex)) {
      processedValue = processedValue.replace(/(\d)-(\d)/g, "$1$2")
    }
    return processedValue
  }

  useEffect(() => {
    const searchQuery = searchParams.get("query") || ""
    setInputValue(searchQuery)
  }, [searchParams])

  useEffect(() => {
    const processedValue = processSearchValue(debouncedInputValue)
    if (processedValue) {
      setSearchParams({ query: processedValue })
    }
  }, [debouncedInputValue, setSearchParams])

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      <PageTitle title="Member search" />
      <div className="tw:flex tw:gap-2">
        <Input
          placeholder="Name, Address, Phone, Email, Member #, Utility #, Business, Referrer Guid, Confirmation Code, Shopper short key, etc."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
        />
      </div>
      <div className="tw:flex tw:justify-end tw:gap-2">
        <Tooltip>
          <TooltipTrigger>
            <Toggle
              variant="outline"
              pressed={showAccounts}
              onPressedChange={setShowAccounts}
            >
              Show Accounts
            </Toggle>
          </TooltipTrigger>
          <TooltipContent side="top">Hide/Show accounts table</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger>
            <Toggle
              variant="outline"
              pressed={showProspects}
              onPressedChange={setShowProspects}
            >
              Show Prospects
            </Toggle>
          </TooltipTrigger>
          <TooltipContent side="top">Hide/Show prospects table</TooltipContent>
        </Tooltip>
      </div>
      {showAccounts && (
        <Card>
          <CardHeader>
            <CardTitle>Accounts</CardTitle>
            <CardAction>
              {getAccountsQuery.isFetching && (
                <Loader2 className="tw:animate-spin" />
              )}
            </CardAction>
          </CardHeader>
          <CardContent>
            <DataTable
              pending={getAccountsQuery.isLoading}
              columns={accountsColumns}
              data={getAccountsQuery.data?.accounts ?? []}
              paginationType="server"
              {...accountsPagination}
              rowCount={getAccountsQuery.data?.total_count}
              onRowClick={(row) =>
                navigate(
                  `/profiles/${row.membership_profile_id}/accounts/${row.account_id}`,
                )
              }
            />
          </CardContent>
        </Card>
      )}

      {showProspects && (
        <Card>
          <CardHeader>
            <CardTitle>Prospects</CardTitle>
            <CardAction>
              {getProspectsQuery.isFetching && (
                <Loader2 className="tw:animate-spin" />
              )}
            </CardAction>
          </CardHeader>
          <CardContent>
            <DataTable
              pending={getProspectsQuery.isLoading}
              columns={prospectsColumns}
              data={getProspectsQuery.data?.prospects ?? []}
              paginationType="server"
              {...prospectsPagination}
              rowCount={getProspectsQuery.data?.total_count}
              onRowClick={(row) => navigate(`/prospect/${row.prospect_guid}`)}
            />
          </CardContent>
        </Card>
      )}
    </div>
  )
}
